/**
 * Terminy liczone w dniach — jedno miejsce dla całego produktu (Etap 53).
 *
 * Arytmetyka dni była do tej pory skopiowana w trzech miejscach (pulpit HR,
 * portal pracownika, silnik podpowiedzi asystenta), a job przypomnień byłby
 * czwartym. Przy trzech kopiach rozjazd „ekran mówi 30 dni, e-mail mówi 31"
 * jest kwestią czasu — a badania i BHP to termin, na którym stoi dopuszczenie
 * do pracy, więc nie miejsce na dwie prawdy.
 *
 * Wszystko tu jest CZYSTE i przyjmuje `today` argumentem. Nie ma tu ani jednego
 * `new Date()` bez argumentu: dzień powstaje na krawędzi (trasa albo job),
 * bo tylko tam wiadomo, w jakiej strefie czasowej go liczyć.
 */
import { CONTRACT_ENDING_SOON_DAYS, HEALTH_EXPIRY_WARN_DAYS } from './constants'

/** Data w formacie rrrr-mm-dd. */
export type IsoDay = string

/**
 * Czego dotyczy przypomnienie o terminie (plan §11) — zarazem klucz
 * w rejestrze wysyłek i wartość enumu w bazie.
 */
export const REMINDER_KINDS = ['badania', 'bhp', 'koniec_umowy'] as const
export type ReminderKind = (typeof REMINDER_KINDS)[number]

/**
 * Próg, na którym przypomnienie powstaje. `po_terminie` to nie próg dni, tylko
 * dzień, w którym termin minął — dlatego ma nazwę, a nie liczbę.
 */
export const REMINDER_STAGES = ['30_dni', '7_dni', 'po_terminie'] as const
export type ReminderStage = (typeof REMINDER_STAGES)[number]

const DAY_MS = 86_400_000

/** Liczba pełnych dni od `from` do `to`; ujemna, gdy `to` już minęło. */
export function daysBetween(from: IsoDay, to: IsoDay): number {
  const start = new Date(`${from}T00:00:00.000Z`).getTime()
  const end = new Date(`${to}T00:00:00.000Z`).getTime()
  return Math.floor((end - start) / DAY_MS)
}

/** Dzień przesunięty o `days` — dodatnie w przyszłość, ujemne w przeszłość. */
export function shiftDay(day: IsoDay, days: number): IsoDay {
  const shifted = new Date(new Date(`${day}T00:00:00.000Z`).getTime() + days * DAY_MS)
  return shifted.toISOString().slice(0, 10)
}

/**
 * Miesiąc „rrrr-mm" przesunięty o `delta` miesięcy — arytmetyka kalendarza,
 * bez stref (fala 9, grupa 89: jedno miejsce zamiast kopii z `toISOString`).
 */
export function shiftMonth(month: string, delta: number): string {
  const [year, index] = month.split('-').map(Number)
  const total = year! * 12 + (index! - 1) + delta
  const shiftedYear = Math.floor(total / 12)
  const shiftedMonth = total - shiftedYear * 12 + 1
  return `${shiftedYear}-${String(shiftedMonth).padStart(2, '0')}`
}

/** Poniedziałek tygodnia, w którym leży dzień kalendarzowy „rrrr-mm-dd". */
export function mondayOf(day: IsoDay): IsoDay {
  const weekday = new Date(`${day}T00:00:00.000Z`).getUTCDay()
  return shiftDay(day, weekday === 0 ? -6 : 1 - weekday)
}

/**
 * Termin przypadający w sobotę albo w dzień ustawowo wolny od pracy (w tym
 * niedzielę) upływa następnego dnia, który takim dniem nie jest — art. 12 § 5
 * Ordynacji podatkowej, do składek przez art. 31 ustawy o systemie ubezpieczeń
 * społecznych; dla wpłat PPK tę samą regułę daje art. 115 k.c. (#10).
 * `holidays` to święta z `holidays_pl` — sobotę i niedzielę rozpoznajemy sami.
 */
export function nextBusinessDay(day: IsoDay, holidays: ReadonlySet<IsoDay>): IsoDay {
  let current = day
  for (;;) {
    const weekday = new Date(`${current}T00:00:00.000Z`).getUTCDay()
    if (weekday !== 0 && weekday !== 6 && !holidays.has(current)) return current
    current = shiftDay(current, 1)
  }
}

/**
 * Stan ważności badania albo szkolenia BHP (§6.2).
 *
 * `err` także przy braku daty: dokument bez terminu ważności nie jest ważny
 * bezterminowo — jest niekompletny, a kadry muszą to widzieć.
 */
export type ValidityState = 'ok' | 'warn' | 'err'

export function validityState(validUntil: IsoDay | null, today: IsoDay): ValidityState {
  if (validUntil === null || validUntil === '') return 'err'
  const days = daysBetween(today, validUntil)
  if (days < 0) return 'err'
  return days <= HEALTH_EXPIRY_WARN_DAYS ? 'warn' : 'ok'
}

/**
 * Czy umowa kończy się „wkrótce" — status pochodny „Kończąca się umowa" (§6.2).
 * Umowa, która już się skończyła, nie jest kończąca się: to inny stan.
 */
export function contractEndingSoon(contractEndAt: IsoDay | null, today: IsoDay): boolean {
  if (contractEndAt === null || contractEndAt === '') return false
  const days = daysBetween(today, contractEndAt)
  return days >= 0 && days <= CONTRACT_ENDING_SOON_DAYS
}

/**
 * Czy zatrudnienie USTAŁO w danym dniu (Fala 6, grupa 105).
 *
 * Ustanie zapisuje offboarding: status 'terminated' i ostatni dzień pracy
 * w `contractEndAt`. Do tego dnia włącznie osoba pracuje, więc ustanie
 * zapisane z wyprzedzeniem nie gasi dopuszczenia do pracy. Status
 * 'terminated' bez daty to także ustanie — tak samo pomija taką osobę silnik
 * płac. Inny status z datą w przeszłości ustaniem nie jest: umowa na czas
 * określony rozwiązuje się z upływem czasu (art. 30 § 1 pkt 4 k.p.), ale
 * tryb, stronę i świadectwo niesie dopiero zapisane ustanie — do tego czasu
 * system nie zgaduje z samej daty.
 */
export function hasEmploymentEnded(
  employee: { readonly status: string; readonly contractEndAt: IsoDay | null },
  today: IsoDay,
): boolean {
  if (employee.status !== 'terminated') return false
  if (employee.contractEndAt === null || employee.contractEndAt === '') return true
  return employee.contractEndAt < today
}

/**
 * Ostatni dzień zatrudnienia z akt, po którym nie planuje się pracy: dzień
 * z `contractEndAt` przy zapisanym ustaniu, '' przy ustaniu bez daty (każdy
 * dzień jest po nim), `null`, gdy ustania nie zapisano.
 */
export function lastEmploymentDay(employee: {
  readonly status: string
  readonly contractEndAt: IsoDay | null
}): IsoDay | '' | null {
  if (employee.status !== 'terminated') return null
  return employee.contractEndAt ?? ''
}

/**
 * Czy zakres dni wychodzi poza okres zatrudnienia (Fala 7, grupa 44).
 *
 * Urlopu udziela się w trakcie stosunku pracy — za urlop niewykorzystany do
 * dnia ustania należy się ekwiwalent (art. 171 § 1 k.p.), a przed pierwszym
 * dniem zatrudnienia urlopu nie ma z czego udzielić. Granice to te same fakty
 * co w rozkładzie: `employedTo` z `lastEmploymentDay` (pusty = ustanie bez
 * daty, więc każdy dzień jest po nim) i `employedFrom` z historii warunków
 * (`null` = nieznany, np. osoba z importu członków — wtedy nie ma dolnej
 * granicy, a nie „wszystko jest przed zatrudnieniem").
 */
export function outsideEmployment(
  employment: { readonly employedFrom: IsoDay | null; readonly employedTo: IsoDay | '' | null },
  from: IsoDay,
  to: IsoDay,
): boolean {
  if (employment.employedTo === '') return true
  if (employment.employedTo !== null && to > employment.employedTo) return true
  return employment.employedFrom !== null && from < employment.employedFrom
}

/**
 * Dzień w strefie czasowej Polski.
 *
 * Wszystkie terminy w produkcie są kolumnami `date`, a dotychczasowy wzorzec
 * `new Date().toISOString().slice(0, 10)` liczy dzień w UTC. Dla trasy, którą
 * ktoś otwiera w godzinach pracy, różnica nie ma znaczenia. Dla joba
 * odpalanego cyklicznie ma: przebieg po 22.00 czasu polskiego (albo po 23.00
 * w czasie letnim) policzyłby dzień POPRZEDNI i wysłał przypomnienie o dzień
 * za wcześnie albo przegapił ostatni dzień terminu.
 */
export function warsawDay(now: Date): IsoDay {
  /* `en-CA` daje rrrr-mm-dd, czyli dokładnie format kolumn `date`. */
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now)
}

/**
 * Rok kalendarzowy w Polsce (Fala 7). Rok salda urlopu, parametrów płac
 * i rejestrów rocznych liczony z doby UTC był w Nowy Rok między północą
 * a 1.00 czasu polskiego jeszcze rokiem poprzednim.
 */
export function warsawYear(now: Date): number {
  return Number(warsawDay(now).slice(0, 4))
}

/**
 * Miesiąc rrrr-mm w Polsce (Fala 7) — domyślny okres ewidencji czasu pracy
 * i rozkładu. Z doby UTC pierwszy dzień miesiąca do 1.00 (zimą) albo 2.00
 * (latem) czasu polskiego był jeszcze miesiącem poprzednim.
 */
export function warsawMonth(now: Date): string {
  return warsawDay(now).slice(0, 7)
}
